import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toggleMenu } from "../../../../utils/helpers.js";
import NotificationBadge from "../../ui/notification-badge/notification-badge.tsx";
import Cookies from "js-cookie";
import { getApi } from "../../../../utils/api.js";
import { API_GET_PENDING_LEAVE_REQUESTS } from "../../../../config/Endpoints.js";
import { useEvent } from "../../../../Hooks/useEvent.js";
import { Event } from "../../../../config/events.js";
import styles from "./side-navbar.module.css";
import {
  LayoutDashboard,
  User,
  Activity,
  CheckSquare,
  Users,
  BarChart3,
  UserMinus,
  Briefcase,
  ChevronDown,
  Settings,
  type LucideIcon,
  Megaphone,
  Calendar,
  ClipboardClock,
  HandCoins,
  ShieldCheck,
  BadgeDollarSign,
  UserCog,
  UserRound,
  Luggage,
  ClipboardCheck,
  CalendarDays,
  Receipt,
  Headphones,
  FolderKanban,
  UsersRound,
  Gauge,
  Layers3,
  FileCheck2,
  MailPlus,
} from "lucide-react";

import {
  type SidebarMenuItem as MenuItem,
  type SidebarSubMenuItem as SubMenuItem,
} from "./normalize-employee-sidebar-menu.ts";
import {
  formatNavigationLabel,
  isRestrictedForSQEEmployeeMenu,
  isRestrictedForSQELabsMenu,
  isSQELabsEmployeeType,
} from "../../../utils/sqe-labs-navigation-access.ts";

interface LeaveDataItem {
  childName: string;
  parentName: string;
  count: number;
}

interface SideNavbarProps {
  menu: MenuItem[];
}

type StoredUserData = {
  empType?: string;
  employeeType?: string;
  type?: string;
  role?: string;
  roleName?: string;
  userRole?: string;
  roles?: string[];
  personalDetail?: {
    empType?: string;
    employeeType?: string;
    type?: string;
  };
};

const CAELIUS_POLICIES_MENU_NAME = "Caelius Policies";
const CAELIUS_CONNECT_MENU_NAME = "Caelius Connect";
const SQE_CONNECT_MENU_NAME = "SQE Connect";
const SQE_EMPLOYEE_TYPES = new Set(["SQE", "SQELABS"]);

function normalizeEmployeeType(value: unknown): string {
  return String(value ?? "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();
}

function getLoggedInEmployeeTypes(): string[] {
  const userData = localStorage.getItem("userData");
  if (!userData) return [];

  try {
    const parsed = JSON.parse(userData) as StoredUserData;
    return [
      parsed.employeeType,
      parsed.empType,
      parsed.type,
      parsed.personalDetail?.employeeType,
      parsed.personalDetail?.empType,
      parsed.personalDetail?.type,
    ].filter(
      (value): value is string =>
        typeof value === "string" && value.trim() !== "",
    );
  } catch (error) {
    console.error("Failed to parse user data from localStorage:", error);
    return [];
  }
}

const MENU_ICONS: Record<string, LucideIcon> = {
  Dashboard: LayoutDashboard,
  "My Profile": User,
  "My Workspace": Briefcase,
  Performance: Activity,
  "Action Center": CheckSquare,
  "Project TeamFlow": Users,
  TeamFlow: Users,
  "Employee Management": Users,
  "Caelius Connect": UsersRound,
  "Analytics & Insights": BarChart3,
  "Utilization Report": Gauge,
  "Employee Offboarding": UserMinus,
  Announcements: Megaphone,
  "Asset Management": Settings,
  "Attendance & Leaves": Calendar,
  "Attendance & Leave": Calendar,
  Timesheets: ClipboardClock,
  Projects: FolderKanban,
  Project: FolderKanban,
  Assessment: ClipboardCheck,
  Events: CalendarDays,
  "IT Requests": Headphones,
  Reimbursements: Receipt,
  "My Reimbursements": HandCoins,
  "Employee Onboarding": UserRound,
  "Salary Management": BadgeDollarSign,
  "Role Management": UserCog,
  "Travel Management": Luggage,
  "Caelius' Policies": ShieldCheck,
  "Tech Skills & Certifications": Layers3,
  "Appraisal Management": FileCheck2,
  "Adhoc Email Setup": MailPlus,
  [CAELIUS_POLICIES_MENU_NAME]: ShieldCheck,
  "SQE Policies": ShieldCheck,
  [CAELIUS_CONNECT_MENU_NAME]: Users,
  [SQE_CONNECT_MENU_NAME]: Users,
};

function renderNavIcon(itemName: string): React.ReactNode {
  if (isCaeliusConnectMenu(itemName)) {
    return <UsersRound size={18} strokeWidth={1.8} />;
  }
  const Icon = MENU_ICONS[itemName];
  if (Icon) return <Icon size={18} strokeWidth={1.8} />;
  return FALLBACK_ICON;
}

function isCaeliusConnectMenu(itemName: string): boolean {
  return itemName.trim().toLowerCase() === "caelius connect";
}

const FALLBACK_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <ChevronDown
      size={14}
      strokeWidth={1.8}
      className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
    />
  );
}


const NotifyDot = () => <span className={styles.notifyDot} aria-hidden />;

export default function SideNavbar({ menu }: SideNavbarProps) {
  const [menuList, setMenuList] = useState<MenuItem[]>([]);
  const { pathname } = useLocation();
  const [activeSubMenu, setActiveSubMenu] = useState<number | null>(null);
  const [leaveData, setLeaveData] = useState<LeaveDataItem[]>([]);
  const accessToken = Cookies.get("accessToken");
  const loggedInEmployeeTypes = useMemo(getLoggedInEmployeeTypes, []);
  const isSQEEmployee = loggedInEmployeeTypes.some((employeeType) =>
    SQE_EMPLOYEE_TYPES.has(normalizeEmployeeType(employeeType)),
  );
  const isSQELabsUser = loggedInEmployeeTypes.some(isSQELabsEmployeeType);

  const preprocessMenuList = (source: MenuItem[]): MenuItem[] => {
    const extraSubItems: SubMenuItem[] = [
      { name: "Attendance Report", achorValue: "/exportAttendance" },
      { name: "Reimbursement Report", achorValue: "/downloadReimbursement" },
      { name: "Utilization Report", achorValue: "/utilizationreport" },
    ];
    return source
      .filter(
        (item) =>
          (!isSQEEmployee ||
            !isRestrictedForSQEEmployeeMenu(item.name)) &&
          (!isSQELabsUser || !isRestrictedForSQELabsMenu(item.name)),
      )
      .map((item) => {
      const itemName = formatNavigationLabel(
        item.name,
        loggedInEmployeeTypes,
      ) ?? item.name;
      const subMenu = item.subMenu
        ?.filter(
          (sub) =>
            (!isSQEEmployee ||
              !isRestrictedForSQEEmployeeMenu(sub.name)) &&
            (!isSQELabsUser || !isRestrictedForSQELabsMenu(sub.name)),
        )
        .map((sub) => ({
          ...sub,
          name: formatNavigationLabel(
            sub.name,
            loggedInEmployeeTypes,
          ) ?? sub.name,
        }));

      if (item.name === "Analytics & Insights" && Array.isArray(item.subMenu)) {
        const updated = [...(subMenu ?? item.subMenu)];
        extraSubItems.forEach((s) => {
          if (isSQELabsUser && isRestrictedForSQELabsMenu(s.name)) return;
          if (!updated.some((e) => e.achorValue === s.achorValue))
            updated.push(s);
        });
        return { ...item, name: itemName, subMenu: updated };
      }
      return { ...item, name: itemName, subMenu };
      });
  };

  const mergeLeaveData = (list: MenuItem[]): MenuItem[] =>
    list.map((item) => {
      let hasNotification = false;
      const updatedSubMenu: SubMenuItem[] =
        item.subMenu?.map((sub) => {
          const found = leaveData.find(
            (ld) => ld.childName === sub.name && ld.parentName === item.name,
          );
          if (found && found.count > 0) {
            hasNotification = true;
            return { ...sub, notificationValue: found.count };
          }
          return sub;
        }) ?? [];
      return { ...item, subMenu: updatedSubMenu, hasNotification };
    });

  const fetchLeaveData = () => {
    getApi<{ data: LeaveDataItem[] }>(
      API_GET_PENDING_LEAVE_REQUESTS,
      accessToken,
    )
      .then((res) => setLeaveData(res.data.data))
      .catch(console.error);
  };

  useEffect(() => {
    fetchLeaveData();
  }, []);

  useEvent(Event.SIDEBAR.NOTIFY_EVENT, fetchLeaveData);

  useEffect(() => {
    if (!menu?.length) return;
    const processed = preprocessMenuList(menu);

    const pIdx = processed.findIndex((i) => i.name === "Project TeamFlow");
    if (pIdx !== -1 && pIdx !== 3) {
      const [item] = processed.splice(pIdx, 1);
      processed.splice(3, 0, item);
    }

    const oIdx = processed.findIndex((i) => i.name === "Employee Offboarding");
    if (oIdx > 0) {
      const [item] = processed.splice(oIdx, 1);
      processed.splice(oIdx - 1, 0, item);
    }

    setMenuList(mergeLeaveData(processed));
  }, [menu, leaveData, isSQEEmployee, isSQELabsUser]);

  return (
    <aside>
      <nav className={styles.sidebar}>
        <div id="hideMenuBtn" className="close-button" onClick={toggleMenu}>
          <span></span>
          <span></span>
        </div>

        <div className={styles.logoWrapper}>
          <a href="/dashboard/" className="text-left ">
            <img
              src={
                isSQEEmployee
                  ? "/assets/sqe_logo.svg"
                  : "/assets/CaeliusOne_logo.svg"
              }
              alt={isSQEEmployee ? "SQE Logo" : "Caelius Consulting Logo"}
              width="150"
            />
          </a>
        </div>
        <div className={styles.divider}></div>
        <div className={`${styles.scrollbarCompact} ${styles.navWrapper}`}>
          <ul
            className={`${styles.navList} d-flex flex-column gap-1 m-0 list-unstyled`}
          >
            {menuList.map((item, index) => {
              const hasSubMenu =
                Array.isArray(item.subMenu) && item.subMenu.length > 0;
              const mainPath = hasSubMenu
                ? null
                : `/dashboard${item.achorValue}`;
              const isActive = pathname === mainPath;
              const isOpen = activeSubMenu === index;

              return (
                <li key={index} className={`list-unstyled ${styles.navItem}`}>
                  {hasSubMenu ? (
                    <button
                      className={`d-flex align-items-center w-100 box-border  rounded gap-3 
text-decoration-none text-start border-0 bg-transparent 
 fw-medium ${styles.navRow} ${isOpen ? styles.navRowOpen : ""}`}
                      onClick={() =>
                        setActiveSubMenu((p) => (p === index ? null : index))
                      }
                      aria-expanded={isOpen.toString()}
                    >
                      <span className={styles.navIcon}>
                        {renderNavIcon(item.name)}
                      </span>
                      <span className={styles.navLabel} title={item.name}>
                        {item.name}
                      </span>
                      {item.hasNotification && <NotifyDot />}
                      <ChevronIcon open={isOpen} />
                    </button>
                  ) : (
                    <Link
                      to={mainPath!}
                      className={`d-flex gap-3 ${styles.navRow} ${isActive ? styles.navRowActive : ""}`}
                    >
                      <span className={styles.navIcon}>
                        {renderNavIcon(item.name)}
                      </span>
                      <span className={styles.navLabel}>{item.name}</span>
                    </Link>
                  )}

                  {hasSubMenu && isOpen && (
                    <ul className={styles.subList}>
                      {item.subMenu!.map((sub, subIdx) => {
                        const subPath = `/dashboard${sub.achorValue}`;
                        const isActiveSub = pathname === subPath;
                        return (
                          <li key={subIdx}>
                            <Link
                              to={subPath}
                              className={`d-flex gap-3 ${styles.subLink} ${isActiveSub ? styles.subLinkActive : ""}`}
                            >
                              {isCaeliusConnectMenu(sub.name) ? (
                                <span className={styles.navIcon}>
                                  {renderNavIcon(sub.name)}
                                </span>
                              ) : null}
                              <span
                                className={styles.subLabel}
                                title={sub.name}
                              >
                                {sub.name}
                              </span>
                              {sub.notificationValue &&
                                sub.notificationValue > 0 && (
                                  <NotificationBadge
                                    count={sub.notificationValue}
                                  />
                                )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
    </aside>
  );
}
